import { useState, useCallback } from 'react'
import { useAuthStore } from '@/store'
import { ChevronDown, ChevronRight } from 'lucide-react'

interface SidebarGroupProps {
  title: string
  icon: React.ReactNode
  children: React.ReactNode
  groupKey: string
}

export function SidebarGroup({ title, icon, children, groupKey }: SidebarGroupProps) {
  const user = useAuthStore(s => s.user)
  const userId = user?.id ?? 'anon'
  const storageKey = `sidebar:v2:${userId}:${groupKey}`
  const DEFAULT_EXPANDED = false
  const [isExpanded, setIsExpanded] = useState(() => {
    try {
      const stored = localStorage.getItem(storageKey)
      return stored !== null ? JSON.parse(stored) : DEFAULT_EXPANDED
    } catch {
      return DEFAULT_EXPANDED
    }
  })

  const toggle = useCallback(() => {
    setIsExpanded((prev: boolean) => {
      const next = !prev
      try {
        localStorage.setItem(storageKey, JSON.stringify(next))
      } catch {
        // Ignore storage errors
      }
      return next
    })
  }, [storageKey])

  return (
    <div className="mb-1">
      <button
        onClick={toggle}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            toggle()
          }
        }}
        className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-slate-400 hover:bg-slate-800 hover:text-white transition cursor-pointer"
        aria-expanded={isExpanded}
      >
        <span className="shrink-0">{icon}</span>
        <span className="flex-1 text-left font-medium text-xs uppercase tracking-wide">{title}</span>
        {isExpanded ? (
          <ChevronDown className="w-3 h-3 text-slate-500 shrink-0" />
        ) : (
          <ChevronRight className="w-3 h-3 text-slate-500 shrink-0" />
        )}
      </button>
      {isExpanded && (
        <div className="ml-4 border-l border-slate-700/50">
          {children}
        </div>
      )}
    </div>
  )
}
